import React, { useMemo } from 'react';

const SectionHeader = ({ styles, title, desc, center = false }) => (
  <div className={`${styles.sectionHeader} ${center ? styles.centerText : ""}`}>
    <h2 className={styles.h2}>{title}</h2>
    <div className={styles.divider} />
    {desc ? <p className={styles.sectionDesc}>{desc}</p> : null}
  </div>
);

const ProgramCard = ({ styles, no, title, subtitle, desc, contents, effects }) => (
  <div className={styles.eduCard}>
    <div className={styles.eduCardTop}>
      <div className={styles.eduNo}>{no}</div>
      <div className={styles.eduTopText}>
        <div className={styles.eduTitle}>{title}</div>
        <div className={styles.eduSubtitle}>{subtitle}</div>
      </div>
    </div>

    <p className={styles.eduDesc}>{desc}</p>

    <div className={styles.eduGrid}>
      <div className={styles.eduMiniCard}>
        <div className={styles.eduMiniTitle}>주요 교육 내용</div>
        <ul className={styles.cleanList}>
          {contents.map((x, i) => (
            <li key={i} className={styles.cleanListItem}>
              <span className={styles.bulletDot} />
              <span className={styles.bulletText}>{x}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.eduMiniCard}>
        <div className={styles.eduMiniTitle}>교육 효과</div>
        <ul className={styles.cleanList}>
          {effects.map((x, i) => (
            <li key={i} className={styles.cleanListItem}>
              <span className={styles.bulletDot} />
              <span className={styles.bulletText}>{x}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const AIEducationSection = ({ styles }) => {
  const programs = useMemo(
    () => [
      {
        no: "01",
        title: "전형적 머신러닝(ML) 코스",
        subtitle: "Machine Learning Fundamentals",
        desc: "데이터 기반 문제 해결을 위한 전통적 머신러닝 알고리즘의 전 과정을 학습합니다.",
        contents: [
          "지도학습·비지도학습·강화학습 기초",
          "회귀/분류/군집화 알고리즘",
          "SVM, k-NN, Decision Tree, Random Forest, Gradient Boosting",
          "모델 평가 (Accuracy, F1, ROC, Cross Validation)",
          "Feature Engineering 실습",
          "실습: Kaggle 스타일 문제 해결 프로젝트",
        ],
        effects: ["기본적인 데이터 분석·예측 모델을 직접 구현 가능", "AI 개발의 기반을 탄탄하게 구축"],
      },
      {
        no: "02",
        title: "ML 기반 응용 AI 프로그램 제작 교육",
        subtitle: "Applied AI Development with ML",
        desc: "머신러닝 모델을 실제 서비스·프로그램에 적용하는 방법을 학습하는 실습 중심 교육입니다.",
        contents: ["Python 기반 ML 모델 개발", "Flask/FastAPI 서버 연동", "모델 배포(MLOps 기초)", "실시간 예측 서비스 개발", "산업별 활용 사례(제조, 의료, 교육, 금융)"],
        effects: ["“모델 개발 → 서비스 적용” 전체 프로세스 경험", "기업 내부 업무 자동화 AI 프로그램 제작 가능"],
      },
      {
        no: "03",
        title: "정통 검색 알고리즘 교육",
        subtitle: "Search Algorithms & A*",
        desc: "A* 및 Heuristic Search를 심층 학습하고, 경로 탐색·의사결정 문제를 실습으로 다룹니다.",
        contents: ["DFS/BFS의 원리", "Uniform Cost Search", "Greedy Search vs A* Search", "Heuristic(휴리스틱)의 개념 및 설계", "게임·로봇 경로 탐색 실습"],
        effects: ["로봇, 게임 AI, 최적화 문제에 Search 알고리즘 적용", "AI 사고력 강화를 통한 고급 알고리즘 이해"],
      },
      {
        no: "04",
        title: "인공지능 수학 교육",
        subtitle: "Mathematics for AI",
        desc: "인공지능 모델을 설계하고 이해하는 데 필요한 핵심 수학 개념을 실무적으로 학습합니다.",
        contents: ["선형대수학 (벡터, 행렬, 고유값)", "미적분학 (경사하강법 이해)", "확률·통계 (분포, 정규화, 베이즈)", "최적화 알고리즘", "머신러닝/딥러닝 수학 해석"],
        effects: ["AI 모델의 내부 동작 원리 이해", "문제 해결에 필요한 수학적 사고력 강화"],
      },
      {
        no: "05",
        title: "딥러닝(Text & 이미지) 적용 교육",
        subtitle: "Deep Learning for Text & Image Processing",
        desc: "최신 딥러닝 기술로 자연어 처리(NLP)와 컴퓨터 비전(CV)을 실습 중심으로 학습합니다.",
        contents: ["CNN, RNN, LSTM, Transformer 기초", "이미지 분류·검출 모델(PyTorch, TensorFlow)", "텍스트 분류, 감성 분석", "BERT 기반 NLP 모델", "Vision+Text 멀티모달 개론"],
        effects: ["딥러닝 기반 고급 인공지능 모델 구현 가능", "실제 데이터 기반 프로젝트 완성"],
      },
      {
        no: "06",
        title: "IoT·로봇 AI 적용 교육",
        subtitle: "AI for IoT & Robotics",
        desc: "로봇·센서·IoT 기기에서 AI를 활용하는 최신 기술을 실습 중심으로 제공합니다.",
        contents: ["센서 데이터 처리", "IoT + Edge AI 개념", "로봇 제어와 강화학습 기초", "경로 탐색(A*) + 객체 인식 결합", "실습: IoT 장치에 AI 모델 탑재"],
        effects: ["전통 ML·딥러닝의 실제 하드웨어 적용 경험", "로봇 및 임베디드 시스템 이해도 향상"],
      },
      {
        no: "07",
        title: "생성형 인공지능 실무 활용 교육",
        subtitle: "Practical Generative AI (LLM, GPT, Diffusion Models)",
        desc: "생성형 AI를 실무에서 활용하는 기술을 집중적으로 학습합니다.",
        contents: ["LLM의 기본 구조와 원리", "ChatGPT API 활용", "이미지/텍스트/코드 생성", "Prompt Engineering 심화", "기업 업무 자동화에서의 LLM 활용"],
        effects: ["기업의 실제 프로젝트에 생성형 AI 즉시 적용 가능", "보고서·문서 자동화, 고객지원 자동화 능력 확보"],
      },
      {
        no: "08",
        title: "생성형 AI 기반 응용 프로그램 제작 교육",
        subtitle: "Building Applications with Generative AI",
        desc: "생성형 AI로 실제 동작하는 AI 제품·서비스를 개발하는 실무 중심 과정입니다.",
        contents: ["ChatGPT/LLM API 응용", "RAG 시스템 구축(문서 기반 AI)", "에이전트 기반 업무 자동화", "LangChain 기반 AI 애플리케이션 개발", "FastAPI/Streamlit을 이용한 LLM 제품 제작"],
        effects: ["기업에서 필요한 생성형 AI 솔루션 직접 구축", "실전 프로젝트 기반 포트폴리오 완성"],
      },
    ],
    []
  );

  const why = useMemo(
    () => [
      "최신 AI 기술 트렌드 기반 커리큘럼 반영",
      "이론 + 실습 + 실전 프로젝트의 통합 교육",
      "산업 현장의 요구(의료·제조·군사·교육 등)를 반영한 실무 중심 설계",
      "ChatGPT·LLM·RAG 등 최신 생성형 AI 기술 교육 강화",
      "IoT·로봇 등 하드웨어 적용까지 아우르는 폭넓은 범위",
      "실제 현업 수준의 최종 프로젝트 제공",
      "기업 맞춤형 커리큘럼 구성 가능",
    ],
    []
  );

  return (
    <>
      {/* Hero */}
      <section className={styles.section}>
        <div className={styles.container}>
          <div className={styles.heroCenter}>
            <h1 className={styles.h1}>인공지능 교육</h1>
            <p className={styles.heroLead}>
              <b>산업 현장에서 바로 활용되는 실무 중심 AI 교육 프로그램</b>
            </p>
            <p className={styles.heroText}>
              MetaAI의 인공지능 교육은 기초 이론 → 핵심 알고리즘 → 응용 AI 개발 → 생성형 AI 실무 활용 → 로봇 · IoT 융합까지
              <b> 전 과정을 아우르는 통합형 커리큘럼</b>을 제공합니다. 최신 AI 기술 트렌드를 반영해 교육 후 즉시 실무에 적용할 수 있는
              <b> 현장형 인재 양성</b>을 목표로 합니다.
            </p>

            <div className={styles.heroTags}>
              <span className={styles.tag}>실무 중심</span>
              <span className={styles.tag}>프로젝트 기반</span>
              <span className={styles.tag}>생성형 AI</span>
              <span className={styles.tag}>로봇·IoT 융합</span>
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className={styles.section} id="ai-education-programs">
        <div className={styles.container}>
          <SectionHeader
            styles={styles}
            title="교육 프로그램 구성 (Programs)"
            desc="기초부터 응용, 생성형 AI, 하드웨어 융합까지 — 단계별 실무 역량을 완성합니다."
          />

          <div className={styles.panel}>
            <div className={styles.panelTopDots}>
              <span className={styles.panelDot} />
              <span className={styles.panelDot} />
              <span className={styles.panelDot} />
            </div>

            <div className={styles.eduList}>
              {programs.map((p) => (
                <ProgramCard key={p.no} styles={styles} {...p} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className={styles.section} id="ai-education-why">
        <div className={styles.container}>
          <SectionHeader styles={styles} title="인공지능 교육의 차별점 (Why Our AI Education?)" />

          <div className={styles.targetWrap}>
            {why.map((t, i) => (
              <div className={styles.targetItem} key={i}>
                {t}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default AIEducationSection